import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { getFailedSubmissionsWithinLast30Days } from '~/platform/shared/claims-appeals';
import * as TrackedItem from '../../utils/trackedItemContent';
import FilesNeeded from '../claim-files-tab/FilesNeeded';

const ActionNeededExplainer = ({ label }) => (
  <va-details
    class="vads-u-display--block vads-u-margin-bottom--3"
    label={label}
  >
    <div>
      <p>This status may stay on your claim for one of these reasons:</p>
      <ul>
        <li>
          <strong>
            We haven’t reviewed your response to an evidence request.
          </strong>{' '}
          The status will go away when we’re done.
        </li>
        <li>
          <strong>
            You submitted files by mail, in person, or through QuickSubmit.
          </strong>{' '}
          We can’t track documents sent those ways yet.
        </li>
      </ul>
    </div>
  </va-details>
);

ActionNeededExplainer.propTypes = {
  label: PropTypes.string.isRequired,
};

const WhatYouNeedToDo = ({ claim, closedTrackedItemsEnabled }) => {
  const {
    evidenceWaiverSubmitted5103,
    trackedItems,
    evidenceSubmissions,
    claimStatusMeta,
  } = claim.attributes;
  const sectionTitle =
    claimStatusMeta?.whatYouNeedToDo?.title || 'What you need to do';
  const emptyStateText =
    claimStatusMeta?.whatYouNeedToDo?.emptyState ||
    'There’s nothing we need from you right now. We’ll let you know when there’s an update.';
  const filesNeeded = trackedItems
    ? // When user indicates they will not be submitting more evidence by adding a standard or automated 5103 waiver,
      // we will remove the automated 5103 request from the filesNeeded array, preventing the alert from showing.
      TrackedItem.getFilesNeeded(trackedItems, closedTrackedItemsEnabled)
    : [];

  const failedSubmissionsWithinLast30Days = useMemo(
    () => getFailedSubmissionsWithinLast30Days(evidenceSubmissions),
    [evidenceSubmissions],
  );

  const hasOpenRequests = filesNeeded.length > 0;
  const nothingNeededMessage = (
    <div className="no-documents">
      <p>{emptyStateText}</p>
    </div>
  );

  return (
    <>
      <h3 className="vads-u-margin-top--0 vads-u-margin-bottom--3">
        {sectionTitle}
      </h3>

      {!hasOpenRequests && (
        <>
          {failedSubmissionsWithinLast30Days.length === 0 &&
            nothingNeededMessage}
          <ActionNeededExplainer label="Why “Action may be needed” is still on your claim" />
        </>
      )}

      {hasOpenRequests && (
        <>
          <p>
            We identified this information as needed to support your claim. We
            accept responses after the request date, but it may delay your
            claim.
          </p>
          <ActionNeededExplainer label="Why requests you’ve already responded to are still on your claim" />
          {/* add explicit role=list to expose the <ul> as a list for Safari/VoiceOver */}
          {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
          <ul className="usa-unstyled-list" role="list">
            {filesNeeded.map(item => (
              <li key={item.id}>
                <FilesNeeded
                  claimId={claim.id}
                  item={item}
                  evidenceWaiverSubmitted5103={evidenceWaiverSubmitted5103}
                  previousPage="status"
                />
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
};

WhatYouNeedToDo.propTypes = {
  claim: PropTypes.object,
  closedTrackedItemsEnabled: PropTypes.bool,
};

export default WhatYouNeedToDo;
